import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { match } from "ts-pattern";

import {
  GetTenantDocument,
  RemoveTenantMembersDocument,
  type RemoveTenantMembersMutation,
  type RemoveTenantMembersMutationVariables,
} from "~/features/admin/tenants/gqls";
import { translateError } from "~/lib/errors";

interface UseTenantRemoveMembersOptions {
  onSuccess?: () => void;
}

/** Remove users from a tenant. */
export function useTenantRemoveMembers({ onSuccess }: UseTenantRemoveMembersOptions = {}) {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [removeMembers] = useMutation<RemoveTenantMembersMutation, RemoveTenantMembersMutationVariables>(
    RemoveTenantMembersDocument,
    { refetchQueries: [GetTenantDocument], awaitRefetchQueries: true },
  );

  const submit = useCallback(
    async (tenantId: string, userIds: string[]) => {
      setProcessing(true);
      try {
        const res = await removeMembers({ variables: { input: { id: tenantId, userIds } } });
        match(res.data?.cnutRemoveTenantMembers)
          .with({ __typename: "ValidationError" }, (e) => toast.error(e.fields.map((f) => f.message).join(", ")))
          .with({ __typename: "TenantRemoveMembersOutput" }, () => {
            toast.success(t("model.tenant.membersRemovedSuccessfully"));
            onSuccess?.();
          })
          .otherwise(() => {});
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [removeMembers, t, onSuccess],
  );

  return { submit, processing };
}
